import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Download, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatCurrency, API_URL } from "../../lib/utils";
import { TPATransactionsTable } from "./TPATransactionsTable";

const PERIODS = [["daily", "Dia"], ["weekly", "Semana"], ["monthly", "Mês"]];
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });

const exportCsv = (data) => {
  const rows = [["Data/hora", "TPA", "Pedido", "Fatura", "Operador", "Valor"], ...data.transactions.map((t) => [t.time, t.tpa_name, t.order_number || "", t.invoice_number || "", t.operator || "", t.amount.toFixed(2)])];
  const blob = new Blob(["\uFEFF" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `tpa_${data.start_date}_${data.end_date}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

// Relatório por TPA (diário / semanal / mensal) para conferir com os extratos do banco
export const TPAReportTab = ({ headers }) => {
  const [period, setPeriod] = useState("daily");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/reports/tpa?period=${period}&date=${date}`, { headers, withCredentials: true });
      setData(r.data);
    } catch { toast.error("Erro ao carregar relatório de TPA"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = data?.by_day || [];
  return (
    <div className="space-y-4 pb-20" data-testid="tpa-report-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="font-['Outfit'] flex items-center gap-2 text-base md:text-lg"><CreditCard className="w-5 h-5 text-blue-500" /> Vendas por TPA</CardTitle>
              <CardDescription>Total e nº de vendas de cada terminal — para conferir com os extratos do banco{data ? ` · ${data.start_date === data.end_date ? data.start_date : `${data.start_date} a ${data.end_date}`}` : ""}</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-md border overflow-hidden">
                {PERIODS.map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setPeriod(id)} className={`px-3 h-9 text-sm ${period === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`tpa-period-${id}`}>{label}</button>
                ))}
              </div>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="tpa-report-date" />
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
              <Button variant="outline" className="h-9 gap-2" onClick={() => data && exportCsv(data)} disabled={!data?.transactions?.length} data-testid="tpa-export-csv"><Download className="w-4 h-4" /> CSV</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border-2 border-blue-500/50 bg-blue-500/5 p-3">
              <p className="text-[11px] uppercase text-muted-foreground">Total TPA no período</p>
              <p className="font-mono font-black text-xl text-blue-600" data-testid="tpa-report-total">{formatCurrency(data?.totals?.total || 0)}</p>
              <p className="text-xs text-muted-foreground">{data?.totals?.count || 0} venda(s){data?.totals?.cancelled_count ? <span className="text-red-500" data-testid="tpa-report-cancelled"> · {data.totals.cancelled_count} anulada(s) ({formatCurrency(data.totals.cancelled_total)}) fora do total</span> : null}</p>
            </div>
            {(data?.tpas || []).map((t) => (
              <div key={t.tpa_id || "none"} className={`rounded-lg border p-3 ${!t.is_active && t.tpa_id ? "opacity-70" : ""}`} data-testid={`tpa-report-card-${t.tpa_id || "none"}`}>
                <p className="text-xs font-semibold truncate">{t.name}{!t.is_active && t.tpa_id ? <span className="text-muted-foreground font-normal"> · inativo</span> : null}</p>
                <p className="text-[11px] text-muted-foreground truncate">{[t.bank_name, t.account_name].filter(Boolean).join(" · ") || "—"}</p>
                <p className="font-mono font-bold text-lg mt-1">{formatCurrency(t.total)}</p>
                <p className="text-xs text-muted-foreground">{t.count} venda(s)</p>
              </div>
            ))}
          </div>

          {days.length > 0 && period !== "daily" && (
            <div className="overflow-x-auto rounded-lg border" data-testid="tpa-by-day-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr><th className="text-left p-2">TPA</th>{days.map((d) => <th key={d.date} className="text-right p-2">{fmtDay(d.date)}</th>)}<th className="text-right p-2">Total</th></tr>
                </thead>
                <tbody>
                  {(data?.tpas || []).filter((t) => t.count).map((t) => (
                    <tr key={t.tpa_id || "none"} className="border-t">
                      <td className="p-2 font-medium">{t.name}</td>
                      {days.map((d) => { const v = t.by_day.find((x) => x.date === d.date); return <td key={d.date} className="p-2 text-right font-mono text-xs">{v ? formatCurrency(v.total) : "—"}</td>; })}
                      <td className="p-2 text-right font-mono font-semibold">{formatCurrency(t.total)}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-muted/30 font-semibold"><td className="p-2">Total</td>{days.map((d) => <td key={d.date} className="p-2 text-right font-mono text-xs">{formatCurrency(d.total)}</td>)}<td className="p-2 text-right font-mono">{formatCurrency(data?.totals?.total || 0)}</td></tr>
                </tbody>
              </table>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Transações ({data?.transactions?.length || 0}) — conferência com o extrato</p>
            <TPATransactionsTable transactions={data?.transactions || []} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
